import React, { useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import VisitorSelection from './components/VisitorSelection';
import VisitorEntries from './components/VisitorEntries';
import PurposeSelection from './components/PurposeSelection';
import GeneralVisitorForm from './components/forms/GeneralVisitorForm';
import DeliveryForm from './components/forms/DeliveryForm';
import InterviewForm from './components/forms/InterviewForm';
import MeetingForm from './components/forms/MeetingForm';
import ContractorForm from './components/forms/ContractorForm';
import MonthlyDocuments from './components/MonthlyDocuments';
import ChequeRegister from './components/ChequeRegister';
import Cashbook from './components/Cashbook';
import DocumentLogBook from './components/DocumentLogBook';
import ClearanceForm from './components/ClearanceForm/ClearanceForm';
import RegisterBook from './components/RegisterBook';
import SalesTargetManagement from './components/SalesTarget/SalesTargetManagement';
import Legal from './pages/Legal/Legal';
import InvoiceManagement from './components/InvoiceManagement/InvoiceManagement';
import AgingReports from './components/AgingReports/AgingReports';
import PayslipDistribution from './components/PayslipDistribution/PayslipDistribution';
import StockReorder from './components/StockReorder/StockReorder';
import DeliveryManagement from './components/DeliveryManagement/DeliveryManagement';
import CustomerRegister from './components/CustomerRegister/CustomerRegister';
import VisionMain from './components/Vision/VisionMain';
import WeeklyMeetings from './components/WeeklyMeetings/WeeklyMeetings';
import SubscriptionPayments from './components/Subscriptions/SubscriptionPayments';
import FileManager from './components/FileManager/FileManager';
import GpReport from './pages/GpReport/GpReport';
import ActionLog from './components/ActionLog/ActionLog';
import WorkingTime from './components/WorkingTime/WorkingTime';
import FrozenPickPortal from './components/FrozenPickPortal/FrozenPickPortal';
import FinancialReports from './components/FinancialReports/FinancialReports';
import AdminDashboard from './components/AdminDashboard';
import ManagerDashboard from './components/ManagerDashboard';
import TaskManagement from './components/TaskManagement';
import RoutineTasks from './components/RoutineTasks';
import UserProfile from './components/UserProfile';
import Unauthorized from './components/Unauthorized';
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedShell from './components/Layout/ProtectedShell';
import HelpWidget from './components/HelpWidget/HelpWidget';
import UpdateNotifier from './components/UpdateNotifier';
import FeedbackForm from './components/FeedbackForm';
import './App.css';

// Main App Component
function AppContent() {
  const { isAuthenticated, user, loading } = useAuth();

  // Role-based landing page redirect - memoized to prevent repeated calculations
  // Must be called before any conditional returns (Rules of Hooks)
  const landingPage = useMemo(() => {
    if (!user) return '/login';
    
    // Check if username is "visitor" - redirect to visitor page
    if (user.username && user.username.toLowerCase() === 'visitor') {
      return '/visitor';
    }
    
    switch (user.role) {
      case 'admin':
        return '/purpose-selection';
      case 'manager':
        return '/purpose-selection';
      case 'employee':
        return '/purpose-selection';
      case 'receptionist':
        return '/purpose-selection';
      case 'accounts':
        return '/purpose-selection';
      case 'visitor':
        return '/purpose-selection';
      default:
        return '/purpose-selection';
    }
  }, [user]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to={landingPage} /> : <Login />}
      />
      <Route path="/visitor" element={<VisitorSelection />} />
      <Route path="/visitor/form/general" element={<GeneralVisitorForm />} />
      <Route path="/visitor/form/delivery" element={<DeliveryForm />} />
      <Route path="/visitor/form/interview" element={<InterviewForm />} />
      <Route path="/visitor/form/meeting" element={<MeetingForm />} />
      <Route path="/visitor/form/contractor" element={<ContractorForm />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      
      {/* Public Feedback Form - No authentication required */}
      <Route path="/feedback/:phone/:invoiceNo" element={<FeedbackForm />} />

      {/*
        Every authenticated page below sits inside <ProtectedShell>, the layout
        route that renders the left navigation. Being a parent route (not a
        wrapper per page) it stays mounted while the user moves between modules,
        so the navigation never rebuilds and its counts are fetched once.
      */}
      <Route element={<ProtectedShell />}>

      {/* Admin Routes */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requiredCard="user-management">
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Manager/CEO Dashboard */}
      <Route
        path="/manager/dashboard"
        element={
          <ProtectedRoute 
            requiredCard="dashboard"
          >
            <ManagerDashboard />
          </ProtectedRoute>
        }
      />

      {/* Task Management - All roles except visitor */}
      <Route
        path="/tasks"
        element={
          <ProtectedRoute 
            requiredCard="task-management"
          >
            <TaskManagement />
          </ProtectedRoute>
        }
      />
      {/* Routine Tasks - same roles as task management */}
      <Route
        path="/tasks/routines"
        element={
          <ProtectedRoute 
            requiredCard="task-management"
          >
            <RoutineTasks />
          </ProtectedRoute>
        }
      />

      {/* User Profile - All authenticated users */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <UserProfile />
          </ProtectedRoute>
        }
      />

      {/* Visitor Entries - Admin, Receptionist */}
      <Route
        path="/visitor-entries"
        element={
          <ProtectedRoute 
            requiredCard="visitor-entries"
          >
            <VisitorEntries />
          </ProtectedRoute>
        }
      />

      {/* Visitor Forms - Admin, Receptionist, Visitor, Accounts, Employee, Manager */}
      <Route
        path="/purpose-selection"
        element={
          <ProtectedRoute>
            <PurposeSelection />
          </ProtectedRoute>
        }
      />

      <Route
        path="/form/general"
        element={
          <ProtectedRoute>
            <GeneralVisitorForm />
          </ProtectedRoute>
        }
      />

      <Route
        path="/form/delivery"
        element={
          <ProtectedRoute>
            <DeliveryForm />
          </ProtectedRoute>
        }
      />

      <Route
        path="/form/interview"
        element={
          <ProtectedRoute>
            <InterviewForm />
          </ProtectedRoute>
        }
      />

      <Route
        path="/form/meeting"
        element={
          <ProtectedRoute>
            <MeetingForm />
          </ProtectedRoute>
        }
      />

      <Route
        path="/form/contractor"
        element={
          <ProtectedRoute>
            <ContractorForm />
          </ProtectedRoute>
        }
      />

      {/* Document Management - All authenticated users */}
      <Route
        path="/document-log-book"
        element={
          <ProtectedRoute 
            requiredCard="document-log-book"
          >
            <DocumentLogBook />
          </ProtectedRoute>
        }
      />

      <Route
        path="/clearance-form"
        element={
          <ProtectedRoute
            requiredCard="clearance-form"
          >
            <ClearanceForm />
          </ProtectedRoute>
        }
      />

      <Route
        path="/register-book"
        element={
          <ProtectedRoute
            requiredCard="register-book"
          >
            <RegisterBook />
          </ProtectedRoute>
        }
      />

      {/* Financial Modules - Admin, Accounts, Receptionist */}
      <Route
        path="/monthly-documents"
        element={
          <ProtectedRoute 
            requiredCard="monthly-documents"
          >
            <MonthlyDocuments />
          </ProtectedRoute>
        }
      />

      <Route
        path="/cheque-register"
        element={
          <ProtectedRoute 
          requiredCard="cheque-register"
          >
          <ChequeRegister />
          </ProtectedRoute>
        }
      />

      <Route
        path="/cashbook"
        element={
          <ProtectedRoute 
            requiredCard="cashbook"
          >
            <Cashbook />
          </ProtectedRoute>
        }
      />

      {/* Sales Target Management */}
      <Route
        path="/sales-target"
        element={
          <ProtectedRoute 
            requiredCard="sales-target-management"
          >
            <SalesTargetManagement />
          </ProtectedRoute>
        }
      />

      {/* Legal Cases — trailing /* because the module owns its own sub-routes
          (dashboard, cases, approvals, reports, masters). */}
      <Route
        path="/legal-cases/*"
        element={
          <ProtectedRoute
            requiredCard="legal-cases"
          >
            <Legal />
          </ProtectedRoute>
        }
      />

      {/* Invoice Management */}
      <Route
        path="/invoice-management"
        element={
          <ProtectedRoute 
            requiredCard="invoice-management"
          >
            <InvoiceManagement />
          </ProtectedRoute>
        }
      />

      {/* Aging Reports */}
      <Route
        path="/aging-reports"
        element={
          <ProtectedRoute 
            requiredCard="aging-reports"
          >
            <AgingReports />
          </ProtectedRoute>
        }
      />

      {/* Payslip Distribution */}
      <Route
        path="/payslip-distribution"
        element={
          <ProtectedRoute
            requiredCard="payslip-distribution"
          >
            <PayslipDistribution />
          </ProtectedRoute>
        }
      />

      {/* Stock Reorder */}
      <Route
        path="/stock-reorder"
        element={
          <ProtectedRoute
            requiredCard="stock-reorder"
          >
            <StockReorder />
          </ProtectedRoute>
        }
      />

      {/* Delivery Management */}
      <Route
        path="/delivery-management"
        element={
          <ProtectedRoute
            requiredCard="delivery-management"
          >
            <DeliveryManagement />
          </ProtectedRoute>
        }
      />

      {/* Customer Register */}
      <Route
        path="/customer-register"
        element={
          <ProtectedRoute
            requiredCard="customer-register"
          >
            <CustomerRegister />
          </ProtectedRoute>
        }
      />

      {/* Strategic Vision */}
      <Route
        path="/vision"
        element={
          <ProtectedRoute
            requiredCard="vision"
          >
            <VisionMain />
          </ProtectedRoute>
        }
      />

      {/* Financial Performance — P&L analytics */}
      <Route
        path="/financial-performance"
        element={
          <ProtectedRoute
            requiredCard="financial-performance"
          >
            <FinancialReports />
          </ProtectedRoute>
        }
      />

      {/* Weekly Meetings — video/PPT uploads + points */}
      <Route
        path="/weekly-meetings"
        element={
          <ProtectedRoute
            requiredCard="weekly-meetings"
          >
            <WeeklyMeetings />
          </ProtectedRoute>
        }
      />

      {/* Subscription Payments — monthly subscriptions + email reminders */}
      <Route
        path="/subscription-payments"
        element={
          <ProtectedRoute
            requiredCard="subscription-payments"
          >
            <SubscriptionPayments />
          </ProtectedRoute>
        }
      />

      {/* File Manager — each user's own SharePoint folder */}
      <Route
        path="/file-manager"
        element={
          <ProtectedRoute
            requiredCard="file-manager"
          >
            <FileManager />
          </ProtectedRoute>
        }
      />

      {/* GP Report — Sales & Gross Profit analysis. Trailing /* because the module
          owns its own sub-routes (dashboard, masters, uploads, reports). */}
      <Route
        path="/gp-report/*"
        element={
          <ProtectedRoute
            requiredCard="gp-report"
          >
            <GpReport />
          </ProtectedRoute>
        }
      />

      {/* Action Log — global tamper-evident record of every write. Admin-only. */}
      <Route
        path="/action-log"
        element={
          <ProtectedRoute
            requiredCard="action-log"
          >
            <ActionLog />
          </ProtectedRoute>
        }
      />

      {/* Working Time — active/idle hours reported by the Windows agent. */}
      <Route
        path="/working-time"
        element={
          <ProtectedRoute
            requiredCard="working-time"
          >
            <WorkingTime />
          </ProtectedRoute>
        }
      />

      {/* Frozen Pick Portal — freezer pick board, frozen item list, billing
          lookup and reports. Ingest from the Chrome extension is a separate,
          API-key-only route (not part of this authenticated app). */}
      <Route
        path="/frozen-pick-portal"
        element={
          <ProtectedRoute
            requiredCard="frozen-pick-portal"
          >
            <FrozenPickPortal />
          </ProtectedRoute>
        }
      />

      </Route>

      {/* Default Routes */}
      <Route path="/" element={<Navigate to={isAuthenticated ? landingPage : '/login'} />} />
      <Route path="*" element={<Navigate to={isAuthenticated ? landingPage : '/login'} />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <UpdateNotifier />
        <AppContent />
        <HelpWidget />
      </AuthProvider>
    </Router>
  );
}

export default App;

